import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { WorldType } from '../types';
import { COLORS, MATERIALS } from '../constants';

interface IsometricBlockProps {
  position: [number, number, number];
  size?: [number, number, number];
  world: WorldType;
  variant?: 'stone' | 'accent' | 'highlight' | 'wood' | 'primary' | 'secondary';
  onClick?: () => void;
  interactive?: boolean;
  hovered?: boolean;
}

export function IsometricBlock({
  position,
  size = [1, 1, 1],
  world,
  variant = 'stone',
  onClick,
  interactive = false,
  hovered = false,
}: IsometricBlockProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;

  const getColor = () => {
    switch (variant) {
      case 'stone':
        return colors.stone;
      case 'accent':
        return colors.accent;
      case 'highlight':
        return colors.highlight;
      case 'wood':
        return world === 'real' ? colors.wood : (colors as any).ethereal || colors.accent;
      case 'primary':
        return colors.primary;
      case 'secondary':
        return colors.secondary;
      default:
        return colors.primary;
    }
  };

  const color = getColor();

  useFrame(() => {
    if (!meshRef.current || !interactive) return;

    // Subtle hover animation
    const targetScale = hovered ? 1.05 : 1;
    meshRef.current.scale.lerp(
      new THREE.Vector3(targetScale, targetScale, targetScale),
      0.1
    );
  });

  return (
    <mesh
      ref={meshRef}
      position={position}
      onClick={onClick}
      castShadow
      receiveShadow
    >
      <boxGeometry args={size} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={MATERIALS.flat.emissiveIntensity}
        roughness={MATERIALS.flat.roughness}
        metalness={MATERIALS.flat.metalness}
        flatShading={MATERIALS.flat.flatShading}
      />
    </mesh>
  );
}

// Flat material helper
function FlatMaterial({ color, opacity = 1 }: { color: string; opacity?: number }) {
  return (
    <meshStandardMaterial
      color={color}
      emissive={color}
      emissiveIntensity={MATERIALS.flat.emissiveIntensity}
      roughness={MATERIALS.flat.roughness}
      metalness={MATERIALS.flat.metalness}
      flatShading={MATERIALS.flat.flatShading}
      transparent={opacity < 1}
      opacity={opacity}
    />
  );
}

// Variant for stairs
interface IsometricStairsProps {
  position: [number, number, number];
  direction: 'north' | 'south' | 'east' | 'west';
  steps?: number;
  world: WorldType;
}

export function IsometricStairs({
  position,
  direction,
  steps = 4,
  world,
}: IsometricStairsProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;

  const getRotation = (): [number, number, number] => {
    switch (direction) {
      case 'north':
        return [0, 0, 0];
      case 'south':
        return [0, Math.PI, 0];
      case 'east':
        return [0, -Math.PI / 2, 0];
      case 'west':
        return [0, Math.PI / 2, 0];
    }
  };

  const stepHeight = 1 / steps;
  const stepDepth = 1 / steps;

  return (
    <group position={position} rotation={getRotation()}>
      {Array.from({ length: steps }).map((_, i) => (
        <mesh
          key={i}
          position={[0, stepHeight * (i + 0.5), -0.5 + stepDepth * (i + 0.5)]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[1, stepHeight, stepDepth]} />
          <FlatMaterial color={colors.stone} />
        </mesh>
      ))}
    </group>
  );
}

// Pillar component - Low poly Monument Valley style
interface IsometricPillarProps {
  position: [number, number, number];
  height?: number;
  world: WorldType;
  opacity?: number;
}

export function IsometricPillar({
  position,
  height = 3,
  world,
  opacity = 1,
}: IsometricPillarProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;

  return (
    <group position={position}>
      {/* Base - square for geometric feel */}
      <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.6, 0.3, 0.6]} />
        <FlatMaterial color={colors.accent} opacity={opacity} />
      </mesh>

      {/* Column - 6 segments for low poly look */}
      <mesh position={[0, height / 2, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.2, 0.25, height - 0.6, 6]} />
        <FlatMaterial color={colors.stone} opacity={opacity} />
      </mesh>

      {/* Capital */}
      <mesh position={[0, height - 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.5, 0.3, 0.5]} />
        <FlatMaterial color={colors.accent} opacity={opacity} />
      </mesh>
    </group>
  );
}

// Arch component
interface IsometricArchProps {
  position: [number, number, number];
  rotation?: [number, number, number];
  world: WorldType;
  opacity?: number;
}

export function IsometricArch({
  position,
  rotation = [0, 0, 0],
  world,
  opacity = 1,
}: IsometricArchProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;

  return (
    <group position={position} rotation={rotation}>
      {/* Left pillar */}
      <mesh position={[-0.75, 1.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.5, 3, 0.5]} />
        <FlatMaterial color={colors.stone} opacity={opacity} />
      </mesh>

      {/* Right pillar */}
      <mesh position={[0.75, 1.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.5, 3, 0.5]} />
        <FlatMaterial color={colors.stone} opacity={opacity} />
      </mesh>

      {/* Top */}
      <mesh position={[0, 3.25, 0]} castShadow receiveShadow>
        <boxGeometry args={[2, 0.5, 0.5]} />
        <FlatMaterial color={colors.accent} opacity={opacity} />
      </mesh>
    </group>
  );
}
